import { useContext, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Checkbox, Form, FormField, FormHelp, Input, Label, LabeledControl, Select } from '@zillow/constellation-10';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm } from '../../../../hooks/index';
import { useSetListingCriteria } from '../../../../hooks/queries/listingCriteria';
import { useMultiplexListingFlow } from '../../../../hooks/queries/multiplex';
import { AppSelectors } from '../../../../selectors/index';
import { useSelector } from '../../../../selectors/useSelector';
import accessUtils from '../../../../utils/accessUtils';
import { postingConstraints } from '../../../../utils/formValidation';
import numberUtils from '../../../../utils/numberUtils';
import wizardActions from '../../../Wizard/actions';

import { TRIGGER_LOCATIONS, TRIGGER_OBJECT } from '../../../../constants/ClickstreamConstants';
import {
  LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS,
  STRING_VALUE_TO_NUMBER_VALUE_MAP,
  TYPES_MAP,
} from '../../../../constants/ListingCriteriaConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import FinancialExpectationsCSEvents from '../../../../model/ClickstreamEvent/FinancialExpectationsCSEvents';
import ListingCriteria from '../../../../model/ListingCriteria';
import Criterion from '../../../../model/ListingCriteria/Criterion';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';

import type { ChangeEvent } from 'react';
import type { UserPropertyCriteriaSetRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';
import type { CriterionProps } from '../../../../model/ListingCriteria/Criterion';

type MockEventProps = {
  target: {
    name: string;
    value: string | boolean;
    checked?: string | boolean;
  };
  persist?: () => void;
};

type SaveEvent = ChangeEvent<HTMLInputElement> | MockEventProps;

type PropTypes = {
  label?: string;
  incomeToRentRatioMax?: string;
};

type FormInputTypes = {
  criterion: string;
  negotiable: boolean;
};

// special value that triggers saving of all input fields
const SAVE_ALL_INPUTS = 'SAVE_ALL_INPUTS';

const criteriaType = TYPES_MAP.INCOME_TO_RENT;

const inputMinRatioName = 'criterion';
const inputNegotiableName = 'negotiable';

const hasValueChanged = (prevValueObject: FormInputTypes, currentValueObj: FormInputTypes) => {
  return (
    prevValueObject[inputMinRatioName] !== currentValueObj[inputMinRatioName] ||
    prevValueObject[inputNegotiableName] !== currentValueObj[inputNegotiableName]
  );
};

const IncomeToRentRatioFormField = ({ label = '', incomeToRentRatioMax = '' }: PropTypes) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { isInModalState, isSaveComplete, saveOutsideComponent, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const {
    cookieEnabledServices,
    currentCriteria,
    currentProperty,
    isTotalMonthlyPriceWithFeeEligible,
    listingAlias,
    price,
    user,
  } = useSelector((state) => {
    return {
      cookieEnabledServices: state.app.cookieEnabledServices,
      currentCriteria: state?.properties?.currentProperty?.listingCriteria?.criteria,
      currentProperty: state?.properties?.currentProperty,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      listingAlias: state?.properties?.currentProperty?.listingAlias,
      price: state?.properties?.currentProperty?.price,
      user: state?.user,
    };
  }, shallowEqual);

  const prevValue = useRef<FormInputTypes | null>();

  const isScreenDesktop = useSelector(AppSelectors.makeIsScreenDesktop);

  const { mutateAsync: updateListingCriteria } = useSetListingCriteria();

  const {
    initCriterionValue,
    initialNegotiableValue = false,
    targetCriteria,
  } = useMemo(() => {
    const foundIndex = currentCriteria?.findIndex((obj: CriterionProps) => obj.type === criteriaType);
    const targetCriteria = currentCriteria?.[foundIndex];

    return {
      initCriterionValue: targetCriteria?.criterion,
      initialNegotiableValue: targetCriteria?.negotiable,
      targetCriteria,
    };
  }, [currentCriteria, criteriaType]);

  const isOptionValid = (option: { value: string | number }) => {
    // always include "Not applicable" option regardless of max credit score
    if (option.value === 'Not applicable') {
      return true;
    }

    // values contain "x" remove it to do the comparison
    const optionValue = option.value.toString().replace('x', '');
    const incomeToRentRatioMaxValue = incomeToRentRatioMax.toString().replace('x', '');

    return Number(optionValue) <= Number(incomeToRentRatioMaxValue);
  };

  const filteredLCMinIncomeToRentRatioOptions = useMemo(() => {
    return incomeToRentRatioMax === ''
      ? LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS
      : LC_MIN_INCOME_TO_RENT_RATIO_OPTIONS.filter(isOptionValid);
  }, [incomeToRentRatioMax]);

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      [inputMinRatioName]: initCriterionValue,
      [inputNegotiableName]: initialNegotiableValue,
    },
    postingConstraints,
  );

  const { active } = currentProperty;
  const triggerLocation = active ? TRIGGER_LOCATIONS.POST_ACTIVATION : TRIGGER_LOCATIONS.POSTING_PATH;
  const triggerObject = isInModalState ? TRIGGER_OBJECT.REVIEW : TRIGGER_OBJECT.FINANCIAL_EXPECTATIONS_PAGE;

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  useEffect(() => {
    if (isSaveComplete) {
      // trigger all inputs to save
      save({
        target: {
          name: SAVE_ALL_INPUTS,
          value: '',
        },
      });
    }
  }, [isSaveComplete]);

  useEffect(() => {
    // @ts-expect-error
    updatePrevValueRef({
      ...inputs,
    });
  }, []);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  /**
   * save
   *
   * if save is coming from modal we can use values from the form inputs
   * if it is not coming from the modal then we need to use data from event.target to handle the race condition from handleCheckboxChange
   */
  const save = (event: SaveEvent) => {
    if (isInMultiplexListingFlow) {
      // TODO: handle save for multiplex listing flow
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value = '', name: fieldName } = event.target;

    let isValueChanging = false;
    if (fieldName !== SAVE_ALL_INPUTS) {
      isValueChanging =
        !!prevValue.current &&
        // @ts-expect-error
        hasValueChanged(prevValue.current, {
          ...inputs,
          // use data from event
          [fieldName]: value,
        });
    } else {
      // @ts-expect-error
      isValueChanging = (prevValue.current && hasValueChanged(prevValue.current, inputs)) || false;
    }

    if (hasFieldError(fieldName, value)) {
      return;
    }

    if (shouldSaveOnBlur == false && isValueChanging === false && saveOutsideComponent === false) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    // saveOutsideComponent check must be above updateIsFetchingData to keep next and prev functionality
    if (saveOutsideComponent) {
      if (isValueChanging === false) {
        // still set fieldName
        actions.addFieldDataEntry({ reducerDispatch, entryObj: { fieldName: criteriaType } });
      } else {
        actions.addFieldDataEntry({
          reducerDispatch,
          entryObj: { fieldName: criteriaType, value: { ...targetCriteria, ...inputs } },
        });
      }

      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    const criterionData = Criterion({
      ...targetCriteria,
      // updates
      ...inputs,
      isEnabled: true,
    });

    // use data from event to handle checkbox changes race condition
    if (fieldName === inputMinRatioName) {
      criterionData.criterion = String(value);
    } else if (fieldName === inputNegotiableName) {
      criterionData.negotiable = Boolean(value);
    }

    const dataArray: Array<CriterionProps> = [criterionData];

    updateListingCriteria({
      getParams: { listingAlias },
      postData: dataArray as Array<UserPropertyCriteriaSetRequestWebDto>,
    })
      .then(() => {
        // sync prev value
        updatePrevValueRef({
          [inputMinRatioName]: criterionData[inputMinRatioName],
          [inputNegotiableName]: !!criterionData[inputNegotiableName],
        });

        dispatch(
          PropertiesActions.updateCurrentProperty({
            listingCriteria: ListingCriteria({
              isEnabled: true,
              criteria: [...currentCriteria, ...dataArray],
            }),
          }),
        );

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: { error: ErrorRMWProps }) => {
        const {
          error: { message },
        } = err;

        updateServerError(message, criteriaType);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updatePrevValueRef = (value: FormInputTypes) => {
    prevValue.current = {
      ...value,
    };
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: SaveEvent) => {
    const { value, name } = event.target;

    const mockEvent = {
      target: {
        name,
        value,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
    FinancialExpectationsCSEvents.IncomeRentRatioCollection({
      currentProperty,
      triggerLocation: triggerLocation,
      triggerObject: triggerObject,
      user,
    });
  };

  const handleCheckboxChange = (event: SaveEvent) => {
    const { checked, name } = event.target;

    if (typeof checked === 'boolean') {
      const mockEvent: SaveEvent = {
        target: {
          name,
          value: checked,
        },
      };

      clearServerError();
      // @ts-expect-error
      handleFormChange(mockEvent);

      if (shouldSaveOnBlur) {
        save(mockEvent);
      }

      FinancialExpectationsCSEvents.IncomeRentNegotiableCheckbox({
        currentProperty,
        triggerLocation: triggerLocation,
        triggerObject: triggerObject,
        user,
      });
    }
  };

  return (
    <Form asChild={true} data-testid="IncomeToRentRatioFormField" onSubmit={(event) => event.preventDefault()}>
      <div>
        {serverErrorObj.fieldName === criteriaType && (
          <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
        )}

        <FormField
          label={<Label required={false}>{label}</Label>}
          control={
            <Select
              size={isScreenDesktop ? 'lg' : 'md'}
              aria-label={label}
              name={inputMinRatioName}
              value={String(inputs[inputMinRatioName] ?? '')}
              onChange={handleChange}
              onBlur={save}
              // @ts-expect-error - error prop is not yet typed in v10
              error={!!errorObj[inputMinRatioName]}
              required={false}
              data-eventid={FinancialExpectationsCSEvents.IncomeRentRatioCollection.data_event_id}
              css={{ whiteSpace: 'pre' }}
            >
              {filteredLCMinIncomeToRentRatioOptions.map(({ label, value }) => {
                return (
                  <option key={value as string} value={value}>
                    {label}
                  </option>
                );
              })}
            </Select>
          }
          description={
            <FormHelp>{`${canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Rent'} is set at ${numberUtils.asCurrency(price)}/month`}</FormHelp>
          }
        />

        {!currentProperty?.isMultifamily && (
          <FormField>
            <LabeledControl
              label={<Label>Income-to-rent ratio is negotiable</Label>}
              control={
                <Checkbox
                  name={inputNegotiableName}
                  checked={inputs[inputNegotiableName] as boolean}
                  onChange={handleCheckboxChange}
                  data-testid="IncomeToRentRatioFormField-NegotiableCheckbox"
                  data-eventid={FinancialExpectationsCSEvents.IncomeRentNegotiableCheckbox.data_event_id}
                />
              }
            />
          </FormField>
        )}

        {Boolean(price) && (
          <FormField
            label={<Label>Minimum monthly pre-tax income</Label>}
            control={
              <Input
                value={
                  inputs[inputMinRatioName] === ''
                    ? '--'
                    : numberUtils.asCurrency(
                        // @ts-expect-error STRING_VALUE_TO_NUMBER_VALUE_MAP?.[inputs[inputMinRatioName]]
                        price * (STRING_VALUE_TO_NUMBER_VALUE_MAP?.[inputs[inputMinRatioName]] ?? 0),
                      )
                }
              />
            }
            readOnly={true}
          />
        )}

        {currentProperty?.isMultifamily && (
          <FormField>
            <LabeledControl
              label={<Label>Income-to-rent ratio is negotiable</Label>}
              control={
                <Checkbox
                  name={inputNegotiableName}
                  checked={inputs[inputNegotiableName] as boolean}
                  onChange={handleCheckboxChange}
                  data-testid="IncomeToRentRatioFormField-NegotiableCheckbox"
                />
              }
            />
          </FormField>
        )}
      </div>
    </Form>
  );
};

export default IncomeToRentRatioFormField;
